// ─── AUTH STORE ───────────────────────────────────────────────────────────────
// Global auth state managed with Zustand.
// Persisted to localStorage for session restoration.
// ─────────────────────────────────────────────────────────────────────────────

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { User, Tenant } from '@aksape/types';

interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: Date | null;
  isAuthenticated: boolean;

  // Actions
  setAuth: (params: {
    user: User;
    tenant: Tenant;
    accessToken: string;
    refreshToken: string;
    expiresAt: Date;
  }) => void;
  updateAccessToken: (accessToken: string, expiresAt: Date) => void;
  clearAuth: () => void;
  updateUser: (user: Partial<User>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      tenant: null,
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
      isAuthenticated: false,

      setAuth: ({ user, tenant, accessToken, refreshToken, expiresAt }) => {
        set({
          user,
          tenant,
          accessToken,
          refreshToken,
          expiresAt,
          isAuthenticated: true,
        });
      },

      updateAccessToken: (accessToken, expiresAt) => {
        set({ accessToken, expiresAt });
      },

      clearAuth: () => {
        set({
          user: null,
          tenant: null,
          accessToken: null,
          refreshToken: null,
          expiresAt: null,
          isAuthenticated: false,
        });
      },

      updateUser: (updates) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        }));
      },
    }),
    {
      name: 'aksape-auth',
      storage: createJSONStorage(() => localStorage),
      // Only persist tokens and basic user info, not sensitive data
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        expiresAt: state.expiresAt,
        isAuthenticated: state.isAuthenticated,
        user: state.user
          ? {
              id: state.user.id,
              tenantId: state.user.tenantId,
              nomeCompleto: state.user.nomeCompleto,
              email: state.user.email,
              perfil: state.user.perfil,
              avatarUrl: state.user.avatarUrl,
            }
          : null,
        tenant: state.tenant
          ? {
              id: state.tenant.id,
              nomeImobiliaria: state.tenant.nomeImobiliaria,
              configuracoes: state.tenant.configuracoes,
            }
          : null,
      }),
    },
  ),
);
